import { useState, useEffect } from 'react';

export type MarketState = 'open' | 'closed' | 'weekend' | 'holiday' | 'error' | 'loading';

interface MarketStatusResponse {
    isOpen: boolean;
    status: MarketState;
    holidayReason: string | null;
}

export function useMarketStatus() {
    const [isOpen, setIsOpen] = useState(false);
    const [status, setStatus] = useState<MarketState>('loading');
    const [holidayReason, setHolidayReason] = useState<string | null>(null);

    useEffect(() => {
        const fetchStatus = async () => {
            try {
                // Fetch from the local Cloudflare Pages Function
                const response = await fetch('/api/market-status');
                if (!response.ok) throw new Error('Failed to fetch market status');

                const contentType = response.headers.get("content-type");
                if (!contentType || !contentType.includes("application/json")) {
                     throw new Error("Received non-JSON response from /api/market-status");
                }

                const data: MarketStatusResponse = await response.json();

                setIsOpen(data.isOpen);
                setStatus(data.status);
                setHolidayReason(data.holidayReason);
            } catch (error) {
                console.error("Error fetching market status:", error);

                // Fallback to local timezone calculation if the backend fails or isn't running locally
                fallbackLocalCalculation();
            }
        };

        // Fallback calculation (does not know about dynamic holidays)
        const fallbackLocalCalculation = () => {
            const now = new Date();
            const options: Intl.DateTimeFormatOptions = { timeZone: 'Asia/Riyadh', hour12: false, weekday: 'short', hour: 'numeric', minute: 'numeric' };
            const formatter = new Intl.DateTimeFormat('en-US', options);
            const parts = formatter.formatToParts(now);

            const day = parts.find(p => p.type === 'weekday')?.value;
            const hour = parseInt(parts.find(p => p.type === 'hour')?.value || '0');
            const minute = parseInt(parts.find(p => p.type === 'minute')?.value || '0');

            const isWorkDay = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu'].includes(day || '');
            const timeInMinutes = hour * 60 + minute;
            const isOpenHour = timeInMinutes >= (10 * 60) && timeInMinutes < (15 * 60);

            const open = isWorkDay && isOpenHour;
            setIsOpen(open);
            setStatus(open ? 'open' : (isWorkDay ? 'closed' : 'weekend'));
            setHolidayReason(null);
        };

        // Fetch on mount
        fetchStatus();

        // Refresh status every 1 minute to keep it up to date
        const interval = setInterval(fetchStatus, 60000);
        return () => clearInterval(interval);
    }, []);

    return { isOpen, status, holidayReason };
}
